/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { Link, useHistory, useParams } from 'react-router-dom';
import Dashboard, { DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { useListDataTransfersQuery, useListSubConnectionsQuery } from '@api/initiative-app-integrations';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { CTAdminBreadcrumbs } from '../../../../components/breadcrumbs/CTAdminBreadcrumbs';
import { ROUTES } from '@constants/routes';
import { INITIATIVE } from '@constants/terminology';
import { generateUrl } from '@routes/util';
import type { DataTransferPlain } from '@g17eco/types/data-transfer';
import { StatusBadge } from '@g17eco/atoms/status-badge';
import { buildSubsidiaryMap } from './utils';

const resolveEntityLabel = (dt: DataTransferPlain, subsidiaryMap: Map<string, string>): string => {
  const ids = [...new Set(dt.reports.map((r) => r.initiativeId))];
  if (ids.length === 1) {
    return subsidiaryMap.get(ids[0]) ?? ids[0];
  }
  return `${ids.length} ${INITIATIVE.PLURAL}`;
};

const SummaryCard = ({ label, value, sublabel, icon }: {
  label: string;
  value: string | number;
  sublabel: string;
  icon: string;
}) => (
  <div className='col'>
    <div className='p-3 whiteBoxContainer'>
      <div className='d-flex align-items-center gap-2 mb-2'>
        <i className={`fa ${icon} text-ThemePrimaryMedium`} />
        <span className='small text-ThemeTextLight text-uppercase fw-bold'>{label}</span>
      </div>
      <div className='h2 fw-bold mb-1'>{value}</div>
      <div className='small text-ThemeTextLight'>{sublabel}</div>
    </div>
  </div>
);

const DataTransferRow = ({ dt, initiativeId, code, subsidiaryMap }: {
  dt: DataTransferPlain;
  initiativeId: string;
  code: string;
  subsidiaryMap: Map<string, string>;
}) => {
  const history = useHistory();
  const created = new Date(dt.created).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  const entityLabel = resolveEntityLabel(dt, subsidiaryMap);
  const jobCount = dt.reports.length;
  const url = generateUrl(ROUTES.APP_INTEGRATIONS_DATA_TRANSFER_SYNC, { initiativeId, code, dataTransferId: dt._id });

  return (
    <tr
      className='align-middle cursor-pointer'
      style={{ borderLeft: '3px solid var(--bs-primary)' }}
      onClick={() => history.push(url)}
    >
      <td className='py-3'>
        <div className='fw-bold'>{dt.name}</div>
        <div className='small text-ThemeTextLight text-capitalize'>{dt.frequency} · {jobCount} jobs</div>
      </td>
      <td className='py-3 text-ThemeTextMedium'>{entityLabel}</td>
      <td className='py-3 text-ThemeTextLight'>{created}</td>
      <td className='py-3'>
        <StatusBadge status='ready' />
      </td>
      <td className='py-3 text-end'>
        <Link
          to={url}
          className='text-ThemePrimaryMedium fw-bold text-decoration-none'
          onClick={(e) => e.stopPropagation()}
        >
          Review <i className='fa fa-long-arrow-right ms-1' />
        </Link>
      </td>
    </tr>
  );
};

const DataTransferList = ({ dataTransfers, initiativeId, code, subsidiaryMap }: {
  dataTransfers: DataTransferPlain[];
  initiativeId: string;
  code: string;
  subsidiaryMap: Map<string, string>;
}) => {
  if (dataTransfers.length === 0) {
    return (
      <DashboardSection>
        <div className='text-center py-5 text-ThemeTextLight'>
          <i className='fa fa-exchange fa-2x mb-3 d-block' />
          No data transfers yet. Create one to start reviewing and syncing integration data.
        </div>
      </DashboardSection>
    );
  }

  const totalJobs = dataTransfers.reduce((sum, dt) => sum + dt.reports.length, 0);
  const allSubsidiaries = new Set(dataTransfers.flatMap((dt) => dt.reports.map((r) => r.initiativeId)));

  return (
    <>
      <div className='dashboard-row mb-3'>
        <div className='row g-3'>
          <SummaryCard label='Transfers' value={dataTransfers.length} sublabel='Data transfers created' icon='fa-exchange' />
          <SummaryCard label='Total Jobs' value={totalJobs} sublabel='Across all transfers' icon='fa-calendar' />
          <SummaryCard label={INITIATIVE.CAPITALIZED_PLURAL} value={allSubsidiaries.size} sublabel={`Unique ${INITIATIVE.PLURAL}`} icon='fa-building' />
        </div>
      </div>
      <DashboardSection padding={0} paddingInternal={0}>
        <table className='table mb-0'>
        <thead>
          <tr className='text-ThemeTextLight text-uppercase small'>
            <th className='ps-4 py-3'>Transfer</th>
            <th className='py-3'>{INITIATIVE.CAPITALIZED_SINGULAR}</th>
            <th className='py-3'>Created</th>
            <th className='py-3'>Status</th>
            <th className='py-3'></th>
          </tr>
        </thead>
        <tbody>
          {dataTransfers.map((dt) => (
            <DataTransferRow key={dt._id} dt={dt} initiativeId={initiativeId} code={code} subsidiaryMap={subsidiaryMap} />
          ))}
        </tbody>
      </table>
      </DashboardSection>
    </>
  );
};

export const DataTransferListPage = () => {
  const { code, initiativeId } = useParams<{ code: string; initiativeId: string }>();
  const isUserStaff = useAppSelector(isStaff);

  const query = useListDataTransfersQuery({ initiativeId, code }, { skip: !isUserStaff });
  const subConnectionsQuery = useListSubConnectionsQuery({ initiativeId, code }, { skip: !isUserStaff });

  if (!isUserStaff) {
    return null;
  }

  return (
    <Dashboard>
      <div className='pl-2 pb-4'>
        <CTAdminBreadcrumbs
          initiativeId={initiativeId}
          breadcrumbs={[
            { label: 'Connections', url: generateUrl(ROUTES.APP_INTEGRATIONS, { initiativeId }) },
            { label: code, url: generateUrl(ROUTES.APP_INTEGRATIONS_VIEW, { initiativeId, code }) },
            { label: 'Data Transfers' },
          ]}
        />
      </div>
      <DashboardSectionTitle
        title={<>Data Transfers <FeatureStability stability='internal' /></>}
        subtitle='Review and reconcile data from your integration provider'
        buttons={[
          <Link
            key='new'
            to={generateUrl(ROUTES.APP_INTEGRATIONS_DATA_TRANSFERS_NEW, { initiativeId, code })}
            className='btn btn-primary'
          >
            <i className='fa fa-plus me-1' />New Data Transfer
          </Link>,
        ]}
      />
      <QueryWrapper query={query} onSuccess={(dataTransfers) => {
        const subsidiaryMap = buildSubsidiaryMap(subConnectionsQuery.data ?? []);
        return (
          <DataTransferList
            dataTransfers={dataTransfers}
            initiativeId={initiativeId}
            code={code}
            subsidiaryMap={subsidiaryMap}
          />
        );
      }} />
    </Dashboard>
  );
};
